/**
 * WGS84 <-> local ENU tangent plane (via ECEF), exact ellipsoid formulas.
 *
 * All points are taken at ellipsoidal height 0. `toENU` returns the east and
 * north components of (ECEF(p) - ECEF(origin)) in the origin's tangent frame
 * (the up component is dropped). `toLatLon` inverts this exactly: it finds the
 * point on the ellipsoid (h = 0) whose ENU projection is (e, n), by Newton
 * iteration with the analytic Jacobian (sub-micrometre after 3-4 steps).
 */
import type { ENU, LatLon, LocalFrame } from '../contracts';

export const WGS84_A = 6378137.0;
export const WGS84_F = 1 / 298.257223563;
export const WGS84_E2 = WGS84_F * (2 - WGS84_F);

const DEG = Math.PI / 180;

class EnuFrame implements LocalFrame {
  readonly origin: LatLon;
  // Origin in ECEF.
  private readonly x0: number;
  private readonly y0: number;
  private readonly z0: number;
  // Rows of the ECEF -> ENU rotation (east and north unit vectors at the origin).
  private readonly ex: number;
  private readonly ey: number;
  private readonly nx: number;
  private readonly ny: number;
  private readonly nz: number;
  private readonly lat0: number;
  private readonly lon0: number;
  // Radii of curvature at the origin (initial guess for the inverse).
  private readonly m0: number;
  private readonly n0: number;

  constructor(origin: LatLon) {
    if (!Number.isFinite(origin.lat) || !Number.isFinite(origin.lon) || Math.abs(origin.lat) >= 90) {
      throw new RangeError(`createLocalFrame: invalid origin ${origin.lat},${origin.lon}`);
    }
    this.origin = Object.freeze({ lat: origin.lat, lon: origin.lon });
    const phi = origin.lat * DEG;
    const lam = origin.lon * DEG;
    const sp = Math.sin(phi);
    const cp = Math.cos(phi);
    const sl = Math.sin(lam);
    const cl = Math.cos(lam);
    const w = 1 - WGS84_E2 * sp * sp;
    const nRad = WGS84_A / Math.sqrt(w);
    this.x0 = nRad * cp * cl;
    this.y0 = nRad * cp * sl;
    this.z0 = nRad * (1 - WGS84_E2) * sp;
    this.ex = -sl;
    this.ey = cl;
    this.nx = -sp * cl;
    this.ny = -sp * sl;
    this.nz = cp;
    this.lat0 = phi;
    this.lon0 = lam;
    this.n0 = nRad;
    this.m0 = (WGS84_A * (1 - WGS84_E2)) / (w * Math.sqrt(w));
  }

  toENU(p: LatLon): ENU {
    return this.project(p.lat * DEG, p.lon * DEG);
  }

  private project(phi: number, lam: number): ENU {
    const sp = Math.sin(phi);
    const cp = Math.cos(phi);
    const nRad = WGS84_A / Math.sqrt(1 - WGS84_E2 * sp * sp);
    const dx = nRad * cp * Math.cos(lam) - this.x0;
    const dy = nRad * cp * Math.sin(lam) - this.y0;
    const dz = nRad * (1 - WGS84_E2) * sp - this.z0;
    return {
      e: this.ex * dx + this.ey * dy,
      n: this.nx * dx + this.ny * dy + this.nz * dz,
    };
  }

  toLatLon(p: ENU): LatLon {
    // Initial guess from the radii of curvature at the origin.
    let phi = this.lat0 + p.n / this.m0;
    let lam = this.lon0 + p.e / (this.n0 * Math.cos(this.lat0));
    for (let i = 0; i < 10; i++) {
      const q = this.project(phi, lam);
      const re = p.e - q.e;
      const rn = p.n - q.n;
      if (Math.abs(re) < 1e-7 && Math.abs(rn) < 1e-7) break;
      // Jacobian: d(ECEF)/d(phi) = M * north_p, d(ECEF)/d(lam) = N cos(phi) * east_p,
      // projected onto the origin's east/north axes.
      const sp = Math.sin(phi);
      const cp = Math.cos(phi);
      const sl = Math.sin(lam);
      const cl = Math.cos(lam);
      const w = 1 - WGS84_E2 * sp * sp;
      const mRad = (WGS84_A * (1 - WGS84_E2)) / (w * Math.sqrt(w));
      const nCos = (WGS84_A / Math.sqrt(w)) * cp;
      // north_p = (-sp cl, -sp sl, cp), east_p = (-sl, cl, 0)
      const a11 = mRad * (this.ex * -sp * cl + this.ey * -sp * sl); // de/dphi
      const a21 = mRad * (this.nx * -sp * cl + this.ny * -sp * sl + this.nz * cp); // dn/dphi
      const a12 = nCos * (this.ex * -sl + this.ey * cl); // de/dlam
      const a22 = nCos * (this.nx * -sl + this.ny * cl); // dn/dlam
      const det = a11 * a22 - a12 * a21;
      phi += (a22 * re - a12 * rn) / det;
      lam += (a11 * rn - a21 * re) / det;
    }
    return { lat: phi / DEG, lon: lam / DEG };
  }

  headingCorrectionDeg(p: LatLon): number {
    // Local north unit vector at p, projected into the origin frame; its frame
    // azimuth is what a true-north heading at p must be rotated by.
    const phi = p.lat * DEG;
    const lam = p.lon * DEG;
    const sp = Math.sin(phi);
    const vx = -sp * Math.cos(lam);
    const vy = -sp * Math.sin(lam);
    const vz = Math.cos(phi);
    const e = this.ex * vx + this.ey * vy;
    const n = this.nx * vx + this.ny * vy + this.nz * vz;
    return Math.atan2(e, n) / DEG;
  }
}

/**
 * Local ENU frame with the given origin (height 0). East of the origin (northern
 * hemisphere) `headingCorrectionDeg` is negative: local north there points
 * slightly west of the frame's +N axis.
 */
export function createLocalFrame(origin: LatLon): LocalFrame {
  return new EnuFrame(origin);
}

/** Normalize an angle in degrees to [0, 360). */
export function normDeg(deg: number): number {
  let r = deg % 360;
  if (r < 0) r += 360;
  return r >= 360 ? 0 : r + 0; // + 0 turns -0 into 0
}
